import React, { ReactNode } from 'react';
import { LucideIcon, X } from 'lucide-react';

interface PromptCardProps {
  icon: LucideIcon;
  headline: string;
  body: string;
  cta?: { label: string; onClick: () => void };
  onDismiss?: () => void;
  // Slot below the CTA for non-standard footers (iOS install hint,
  // "no volver a mostrar" link). Most prompts won't use it.
  children?: ReactNode;
}

// Unified visual primitive for dashboard engagement prompts. Style modeled
// on the existing PendingInvitationsBanner (rose-50 bg + rose-200 border-2
// + flat icon + sans semibold headline) so all dashboard "we suggest you
// do X" surfaces look the same. See DESIGN.md §3.3.
const PromptCard: React.FC<PromptCardProps> = ({
  icon: Icon,
  headline,
  body,
  cta,
  onDismiss,
  children,
}) => {
  return (
    <div className="bg-rose-50 border-2 border-rose-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <Icon className="h-6 w-6 text-rose-600 flex-shrink-0 mt-0.5" />

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-rose-900 mb-1">
            {headline}
          </h3>
          <p className="text-sm text-rose-800 leading-relaxed">
            {body}
          </p>

          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 h-9 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors"
            >
              {cta.label}
            </button>
          )}

          {children}
        </div>

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Cerrar"
            className="flex-shrink-0 text-rose-400 hover:text-rose-600 transition-colors !min-h-0 !min-w-0"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PromptCard;
